import { useState } from "react"
import "../App.css" // Gerekli CSS dosyası
import { monthNames } from "../utils/settings"
import { linkifyHtml } from "../utils/textUtils"

/**
 * MiniCard komponenti - Timeline event'lerini daha modüler bir yapıda göstermek için kullanılır
 *
 * @param {Object} event - Gösterilecek event objesi
 * @param {Array} categories - Tüm kategorilerin dizisi
 * @param {Array} stylings - Tüm stillerin dizisi
 * @param {Function} onClick - Karta tıklandığında çalışacak fonksiyon
 * @param {number} zIndex - Kartın z-index değeri
 * @param {number} marginBottom - Kartın alt margin değeri
 * @param {boolean} isSelected - Seçili olup olmadığını belirten flag
 * @param {number} endLinePosition - Bitiş çizgisi için yeni prop
 * @param {string} storyStartDate - Hikayenin başlangıç tarihi
 * @param {string} storyEndDate - Hikayenin bitiş tarihi
 * @returns {JSX.Element} - MiniCard komponenti
 */
const MiniCard = ({
	event,
	categories, // Array of all categories
	stylings, // Array of all stylings
	onClick,
	zIndex = 10,
	marginBottom = 0,
	isSelected = false,
	storyStartDate,
	storyEndDate,
}) => {
	const [isZoomed, setIsZoomed] = useState(false)
	const category = categories.find((cat) => cat.id === event.categoryId)
	const styling = stylings[0] || {}
	// Get the category for this event - handle both object and id reference cases
	let categoryObj

	// Case 1: If category_id is a full category object, use it directly
	if (
		event.category_id &&
		typeof event.category_id === "object" &&
		event.category_id.color
	) {
		categoryObj = event.category_id
	}
	// Case 2: Otherwise try to find the category by id in the categories array
	else if (event.category_id) {
		categoryObj = categories.find((cat) => cat._id === event.category_id)
	}

	// Determine styling based on styling_id
	const getStyleValues = () => {
		// Check if event has a styling_id reference
		if (event.styling_id) {
			// Try to find the styling in the stylings array
			if (Array.isArray(stylings)) {
				try {
					const eventStyling = stylings.find(
						(style) =>
							style._id === event.styling_id ||
							style.id === event.styling_id ||
							String(style._id) === String(event.styling_id)
					)

					if (eventStyling) {
						return {
							width: eventStyling.width || 150,
							height: eventStyling.height || 120,
							borderRadius: eventStyling.borderRadius || 6,
						}
					}
				} catch (error) {
					console.error("Error finding event style:", error)
				}
			}
		}

		// Default values when no styling_id is found
		return {
			width: event.width,
			height: event.height,
			borderRadius: event.borderRadius,
		}
	}

	const stylingObj = getStyleValues()

	// Resim URL'i al
	const getImageUrl = (imageId) => {
		// Resim ID'si varsa ve sayı ise onu kullan
		if (imageId && !isNaN(parseInt(imageId))) {
			return `/${imageId}.jpg`
		} else if (imageId && typeof imageId === "string") {
			// Eğer imageId bir string ise ve / ile başlıyorsa doğrudan kullan
			if (imageId.startsWith("/")) {
				return imageId
			}
			// Aksi halde / ekleyerek kullan
			return `/${imageId}`
		}
		// Varsayılan SVG placeholder
		return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3EGörsel Yok%3C/text%3E%3C/svg%3E"
	}

	// Resim hata işleyicisi
	const handleImageError = (e) => {
		console.error("MiniCard Resim yüklenemedi:", e.target.src)

		try {
			// Get the current image URL
			const imgSrc = e.target.src

			// If the URL is from React dev server, try with backend server instead
			if (imgSrc.includes("localhost:3000")) {
				const newSrc = imgSrc.replace(
					"localhost:3000",
					new URL(process.env.REACT_APP_API_URL).host
				)
				console.log("Trying backend URL instead:", newSrc)
				e.target.src = newSrc
				return
			}

			// If there's a relative path starting with /uploads, try with full backend URL
			if (imgSrc.startsWith("/uploads")) {
				const newSrc = `${process.env.REACT_APP_API_URL}${imgSrc}`
				console.log("Converting relative URL to absolute:", newSrc)
				e.target.src = newSrc
				return
			}

			// If nothing works, show a SVG placeholder
			e.target.src =
				"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3EGörsel Yok%3C/text%3E%3C/svg%3E"
		} catch (error) {
			console.error("MiniCard Alternatif resim yükleme hatası:", error)
			// Set placeholder as fallback
			e.target.src =
				"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3EGörsel Yok%3C/text%3E%3C/svg%3E"
		}
	}

	// Tarihten ay ve yıl bilgisini alma
	const formatDate = (date) => {
		if (!date) return ""

		const year = date.getFullYear()
		const month = date.getMonth()
		const day = date.getDate()

		if (year < 0) {
			return `${day} ${monthNames[month]} MÖ ${Math.abs(year)}`
		} else {
			return `${day} ${monthNames[month]} ${year}`
		}
	}

	// Karta tıklama işleyicisi
	const handleClick = () => {
		setIsZoomed(true)

		// Event objesini güncelleyerek gönder
		const enrichedEvent = {
			...event,
			// Don't modify the event data structure unless necessary
			images: event.images || [],
			videos: event.videos || [],
			map_images: event.map_images || [],
			mapImages: event.mapImages || event.map_images || [],
		}

		if (onClick) {
			console.log(
				"MiniCard içinden onClick çağrılıyor... Event:",
				enrichedEvent
			)
			onClick(enrichedEvent)

			if (window.selectEvent) {
				window.selectEvent(enrichedEvent)
			}
		}

		document.dispatchEvent(
			new CustomEvent("selectTimelineEvent", { detail: enrichedEvent })
		)

		setTimeout(() => {
			setIsZoomed(false)
		}, 300)
	}

	const containerStyle = {
		position: "relative",
		marginBottom: marginBottom,
		zIndex,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	}

	const cardStyle = {
		width: stylingObj.width || 150,
		minHeight: event.showDescription ? stylingObj.height || 110 : "auto",
		borderRadius: stylingObj.borderRadius || 6,
		backgroundColor: "#fff",
		border: `1px solid ${categoryObj?.color || "#808080"}`,
		overflow: "hidden",
		cursor: "pointer",
		transition: "all 0.3s ease",
		transform: isZoomed ? "scale(1.02) translateX(3px)" : "scale(1)",
		boxShadow: isZoomed
			? "0 4px 8px rgba(0,0,0,0.1)"
			: "0 2px 4px rgba(0,0,0,0.05)",
		position: "relative",
		zIndex: 2,
	}

	// Render cover content (video or image)
	const renderCover = () => {
		// Process image URL to ensure it works
		const processImageUrl = (url) => {
			if (!url) return null

			// If it's already an absolute URL, return as is
			if (url.startsWith("http")) {
				return url
			}

			// If it's a relative URL starting with /uploads, convert to backend URL using env variable
			if (url.startsWith("/uploads")) {
				return `${process.env.REACT_APP_API_URL}${url}`
			}

			// Otherwise, use as is
			return url
		}

		// YouTube video thumbnail extraction function
		const getYoutubeVideoThumbnail = (url) => {
			if (!url) return null

			// Match YouTube URL patterns
			const youtubeRegex =
				/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i
			const match = url.match(youtubeRegex)

			if (match && match[1]) {
				// Return high quality thumbnail
				return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`
			}

			return null
		}

		// If there's a coverContent property (primary way to determine what to show)
		if (event.coverContent) {
			// Check if this is a YouTube video and extract thumbnail
			const youtubeThumbnail = getYoutubeVideoThumbnail(
				event.coverContent
			)

			if (youtubeThumbnail) {
				return (
					<div
						style={{
							width: "100%",
							height: "60%",
							overflow: "hidden",
							position: "relative",
						}}
						draggable="false"
					>
						<img
							src={youtubeThumbnail}
							alt={event.title}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
							}}
							draggable="false"
							onError={handleImageError}
						/>
						{/* YouTube play button overlay */}
						<div
							style={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								width: "40px",
								height: "40px",
								backgroundColor: "rgba(255, 0, 0, 0.8)",
								borderRadius: "50%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								color: "white",
								fontSize: "16px",
							}}
							draggable="false"
						>
							▶
						</div>
					</div>
				)
			} else {
				return (
					<div
						style={{
							width: "100%",
							height: "60%",
							overflow: "hidden",
							position: "relative",
						}}
						draggable="false"
					>
						<img
							src={processImageUrl(event.coverContent)}
							alt={event.title}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
							}}
							onError={handleImageError}
							draggable="false"
						/>
					</div>
				)
			}
		}

		// Eski cover kontrolü (backwards compatibility)
		if (event.cover) {
			if (event.cover.type === "video") {
				// Check if this is a YouTube video
				const youtubeThumbnail = getYoutubeVideoThumbnail(
					event.cover.url
				)

				if (youtubeThumbnail) {
					return (
						<div
							style={{
								width: "100%",
								height: "60%",
								overflow: "hidden",
								position: "relative",
							}}
						>
							<img
								src={youtubeThumbnail}
								alt={event.title}
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
								}}
								onError={handleImageError}
							/>
							{/* YouTube play button overlay */}
							<div
								style={{
									position: "absolute",
									top: "50%",
									left: "50%",
									transform: "translate(-50%, -50%)",
									width: "40px",
									height: "40px",
									backgroundColor: "rgba(255, 0, 0, 0.8)",
									borderRadius: "50%",
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									color: "white",
									fontSize: "16px",
								}}
							>
								▶
							</div>
						</div>
					)
				} else {
					return (
						<video
							src={event.cover.url}
							className="w-full h-auto"
							controls
						/>
					)
				}
			} else if (event.cover.type === "image") {
				return (
					<img
						src={event.cover.url}
						alt={event.title}
						className="w-full h-auto"
					/>
				)
			}
		}

		// If we reach here, there's no valid cover content to display
		return null
	}

	return (
		<div className="mini-card-container" style={containerStyle}>
			<div className="mini-card" style={cardStyle}>
				{/* Tarih etiketi - Kartın üstünde kalacak */}
				<div
					className="timeline-date"
					style={{
						position: "absolute",
						left: "25px",
						top: "-20px",
						fontSize: "0.75rem",
						fontWeight: "600",
						color: "#666",
						backgroundColor: "#fff",
						padding: "2px 6px",
						borderRadius: "3px",
						boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
						zIndex: 2,
					}}
				>
					{formatDate(event.startDate)}
					{event.endDate && ` - ${formatDate(event.endDate)}`}
				</div>

				<div
					className={`timeline-card ${isSelected ? "selected" : ""} ${isZoomed ? "zoomed" : ""
						}`}
					style={{
						width: stylingObj.width || 150,
						minHeight: event.showDescription
							? stylingObj.height || 110
							: "auto",
						borderRadius: stylingObj.borderRadius || 6,
						backgroundColor: "#fff",
						border: `1px solid ${categoryObj?.color || "#808080"}`,
						overflow: "hidden",
						cursor: "pointer",
						transition: "all 0.3s ease",
						transform: isZoomed
							? "scale(1.02) translateX(3px)"
							: "scale(1)",
						boxShadow: isZoomed
							? "0 4px 8px rgba(0,0,0,0.1)"
							: "0 2px 4px rgba(0,0,0,0.05)",
						position: "relative",
						zIndex: 3,
					}}
					onClick={handleClick}
					onMouseEnter={() => setIsZoomed(true)}
					onMouseLeave={() => setIsZoomed(false)}
				>
					{/* İçerik alanı - Şimdi önce resim geliyor */}
					<div
						className="card-content"
						style={{
							padding: 0, // Padding'i kaldırdık çünkü resim tam genişlikte olacak
						}}
						draggable="false"
					>
						{renderCover()}
					</div>

					{/* Başlık alanı - Şimdi resmin altında */}
					<div
						className="card-header"
						style={{
							padding: "1px 3px",
							borderTop: "1px solid #eee",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							backgroundColor: categoryObj?.color || "#808080",
							color: "#fff",
							minHeight: "18px",
							overflow: "hidden", // Taşmaları engellemek için
						}}
					>
						<h3
							className="rich-text-content"
							style={{
								margin: 0,
								fontSize: "0.7rem",
								fontWeight: "600",
								lineHeight: "1.1",
								whiteSpace: "nowrap", // Alt satıra geçmeyi engelle
								overflow: "hidden", // Taşan kısmı gizle
								textOverflow: "ellipsis", // ... koy
								width: "100%", // Tam genişlik kullan
								display: "block", // Block element gibi davran
							}}
							dangerouslySetInnerHTML={{ __html: event.title }}
						/>
					</div>

					{/* Açıklama alanı - Başlığın altında - UPDATED */}
					{event.showDescription && event.description && (
						<div
							style={{
								padding: "2px 4px",
								width: "95%",
								margin: "0 auto",
								maxHeight: "40px",
								overflow: "hidden",
							}}
						>
							<div
								style={{
									margin: "2px 0",
									fontSize: "0.7rem",
									color: "#666",
									lineHeight: "1.2",
									display: "-webkit-box",
									WebkitBoxOrient: "vertical",
									WebkitLineClamp: 1,
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
								className="truncate rich-text-content"
								dangerouslySetInnerHTML={{
									__html: linkifyHtml(event.description),
								}}
							/>
						</div>
					)}

					{/* Seçili durum göstergesi */}
					{isSelected && (
						<div
							style={{
								position: "absolute",
								top: 0,
								right: 0,
								padding: "2px 6px",
								backgroundColor:
									categoryObj?.color || "#3b82f6",
								color: "#fff",
								fontSize: "0.7rem",
								borderBottomLeftRadius: "6px",
							}}
						>
							Seçili
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default MiniCard
